import type { TranslationKey } from "@privacy/translations";
import type { FC } from "react";
import { Box, Typography, Stack } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import type { Theme } from "@mui/material/styles";
import { SettingsIcon } from "../../../shared/icons";
import type { TemplateFormType } from "../../../api/templateFormSettings.types";

type FormTemplateCardProps = {
  form: TemplateFormType;
  onEditClick: () => void;
  onHover: () => void;
};

export const TemplateFormCard: FC<FormTemplateCardProps> = ({ form, onEditClick, onHover }) => {
  const { formType, updatedAt } = form;
  const { labelKey } = formTypeToTextAndStyle[formType];

  const { t, formatDate } = useTranslation();

  return (
    <Box
      data-testid={"Settings.Template.Edit." + formType}
      component="button"
      onClick={onEditClick}
      onMouseEnter={onHover}
      sx={{
        "all": "unset",
        "height": "54px",
        "cursor": "pointer",
        "&:hover,&:focus-visible": { backgroundColor: theme => theme.palette.action.hover },
        "px": 3,
        "py": 2,
        "border": "1px solid",
        "borderColor": theme => theme.palette.grey["400"],
        "borderLeft": "4px solid",
        "borderLeftColor": formTypeToTextAndStyle[formType].borderColor,
        "borderRadius": 1,
      }}
    >
      <Stack direction="row" sx={{ height: "100%", gap: 2, justifyContent: "space-between" }}>
        <Stack sx={{ height: "100%", justifyContent: "space-between" }}>
          <Typography variant="h2" sx={{ lineHeight: 1.2, whiteSpace: "nowrap", fontWeight: "600" }}>
            {t(labelKey)}
          </Typography>
          <Typography
            sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}
          >
            {t("Settings.Template.subHeader.HelperText")}
            {formatDate(updatedAt, { showTime: true })}
          </Typography>
        </Stack>

        <Box sx={{ alignSelf: "flex-start" }}>
          <SettingsIcon color={theme => theme.palette.text.tertiary} size={20} />
        </Box>
      </Stack>
    </Box>
  );
};

export const formTypeToTextAndStyle: Record<
  TemplateFormType["formType"],
  { labelKey: TranslationKey; borderColor: (theme: Theme) => string }
> = {
  REGISTRATIONS: {
    labelKey: "RegistrationGrid.Controller.Title",
    borderColor: theme => theme.palette.brandBlue["500"],
  },
  PRIVACY_PROCESSOR_REGISTRATIONS: {
    labelKey: "RegistrationGrid.Processor.Title",
    borderColor: theme => theme.palette.brandBlue["500"],
  },
  SYSTEMS: { labelKey: "SystemsGrid.Title", borderColor: theme => theme.palette.purple["400"] },
  PERSONAL_DATA_CONTROLLERS: {
    labelKey: "PersonalDataControllersGrid.Header",
    borderColor: theme => theme.palette.brandRed["500"],
  },
  DATA_PROTECTION_IMPACT_ASSESSMENT: { labelKey: "Dpia.Grid.Title", borderColor: theme => theme.palette.teal["500"] },
};
